import React, { useState, useEffect } from 'react';
import DraggableModal from './DraggableModal';
import { lazyWithRetry } from '../utils/lazyWithRetry';
const AuthModal = lazyWithRetry(
    () =>
        import('./PasswordlessLogin').then((module) => ({
            default: module.AuthModal,
        })),
    'lazy-retry:auth-modal'
);
const BetaSignupModal = React.lazy(() => import('./BetaSignupModal'));
const UnsubscribeModal = React.lazy(() => import('./UnsubscribeModal'));
const DeleteAccountModal = React.lazy(() => import('./DeleteAccountModal'));
import { useAuthContext } from '../contexts/AuthContext';
import { HTTP_API_URL } from '../config/api';
import { authService } from '../services/authService';
import './DownloadBar.css';

export const DOWNLOAD_BAR_WIDTH = 360;
export const DOWNLOAD_BAR_COLLAPSED_WIDTH = 88;

const formatDataAmount = (bytes?: number | null): string | null => {
    if (bytes === null || bytes === undefined || Number.isNaN(bytes)) {
        return null;
    }

    const safeBytes = Math.max(bytes, 0);
    const GB = 1024 * 1024 * 1024;
    const MB = 1024 * 1024;

    if (safeBytes >= GB) {
        return `${(safeBytes / GB).toFixed(2)} GB`;
    }
    if (safeBytes >= MB) {
        return `${(safeBytes / MB).toFixed(2)} MB`;
    }
    if (safeBytes >= 1024) {
        return `${(safeBytes / 1024).toFixed(0)} KB`;
    }
    return `${safeBytes} bytes`;
};

const getUsagePercent = (usedBytes?: number | null, limitBytes?: number): number => {
    if (
        usedBytes === null ||
        usedBytes === undefined ||
        limitBytes === null ||
        limitBytes === undefined ||
        limitBytes <= 0
    ) {
        return 0;
    }

    const ratio = Math.min(Math.max(usedBytes / limitBytes, 0), 1);
    return Number((ratio * 100).toFixed(2));
};

const parseBillingDate = (value: unknown): Date | null => {
    if (!value) {
        return null;
    }

    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }

    if (typeof value === 'number') {
        const millis = value > 1_000_000_000_000 ? value : value * 1000;
        const date = new Date(millis);
        return Number.isNaN(date.getTime()) ? null : date;
    }

    if (typeof value === 'string') {
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? null : date;
    }

    if (typeof value === 'object') {
        const timestampLike = value as {
            seconds?: number;
            _seconds?: number;
            toDate?: () => Date;
        };

        if (typeof timestampLike.toDate === 'function') {
            const date = timestampLike.toDate();
            return Number.isNaN(date.getTime()) ? null : date;
        }

        const secondsValue = timestampLike.seconds ?? timestampLike._seconds;
        if (typeof secondsValue === 'number') {
            const date = new Date(secondsValue * 1000);
            return Number.isNaN(date.getTime()) ? null : date;
        }
    }

    return null;
};

const getLatestDate = (values: unknown[]): Date | null => {
    let latest: Date | null = null;
    for (const value of values) {
        const parsed = parseBillingDate(value);
        if (!parsed) continue;
        if (!latest || parsed.getTime() > latest.getTime()) {
            latest = parsed;
        }
    }
    return latest;
};

const formatBillingPeriodEnd = (value?: unknown): string | null => {
    if (!value) {
        return null;
    }

    const date = parseBillingDate(value);
    if (!date) {
        return null;
    }

    return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    }).format(date);
};

interface DownloadBarProps {
    setShowPurchaseModal: (show: boolean) => void;
    remainingDownloadBytes?: number | null;
    usedDownloadBytes?: number | null;
    downloadLimitBytes?: number;
    variant?: 'fixed' | 'drawer';
    isOpen?: boolean;
    onClose?: () => void;
    isCollapsed?: boolean;
    onToggleCollapsed?: () => void;
    hasActiveSubscription?: boolean;
    themePreference: 'system' | 'light' | 'dark';
    effectiveTheme: 'light' | 'dark';
    onThemeChange: (value: 'system' | 'light' | 'dark') => void;
    onRequestAuthModal?: () => void;
    onPrefetchPurchaseModal?: () => void;
    storageUsageBytes?: number;
    storageCapBytes?: number;
}

const DownloadBar: React.FC<DownloadBarProps> = ({
    setShowPurchaseModal,
    remainingDownloadBytes,
    usedDownloadBytes,
    downloadLimitBytes,
    variant = 'fixed',
    isOpen = true,
    onClose,
    isCollapsed = false,
    onToggleCollapsed,
    hasActiveSubscription = false,
    themePreference,
    effectiveTheme,
    onThemeChange,
    onRequestAuthModal,
    onPrefetchPurchaseModal,
    storageUsageBytes,
    storageCapBytes,
}) => {
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [showBetaSignupModal, setShowBetaSignupModal] = useState(false);
    const [showUnsubscribeModal, setShowUnsubscribeModal] = useState(false);
    const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
    const [showUnsubscribeSuccess, setShowUnsubscribeSuccess] = useState(false);
    const [unsubscribeSuccessMessage, setUnsubscribeSuccessMessage] = useState(
        'Your subscription will remain active until the end of your current paid billing period. It will then be cancelled automatically.'
    );
    const [showKeepSubscriptionConfirm, setShowKeepSubscriptionConfirm] = useState(false);
    const [showKeepSubscriptionSuccess, setShowKeepSubscriptionSuccess] = useState(false);
    const [keepSubscriptionMessage, setKeepSubscriptionMessage] = useState(
        'Your subscription will continue and renew normally.'
    );
    const [keepSubscriptionLoading, setKeepSubscriptionLoading] = useState(false);
    const { user, profile, signOut } = useAuthContext();
    const subscriptionData = profile?.subscription as
        | {
            status?: string;
            cancel_at_period_end?: boolean;
            current_period_end?: unknown;
            scheduled_cancellation_at?: unknown;
            cancel_at?: unknown;
            ended_at?: unknown;
            cancellation_requested_at?: unknown;
            canceled_at?: unknown;
        }
        | undefined;
    const isCancellationAlreadyScheduled = Boolean(
        subscriptionData?.status === 'active' &&
        subscriptionData?.cancel_at_period_end
    );
    const cancellationRequestedDate = parseBillingDate(
        subscriptionData?.cancellation_requested_at ?? subscriptionData?.canceled_at
    );
    const scheduledEndDate = getLatestDate([
        subscriptionData?.current_period_end,
        subscriptionData?.scheduled_cancellation_at,
        subscriptionData?.cancel_at,
        subscriptionData?.ended_at,
    ]);
    const effectiveEndDate =
        cancellationRequestedDate && scheduledEndDate &&
            scheduledEndDate.getTime() <= cancellationRequestedDate.getTime()
            ? null
            : scheduledEndDate;
    const scheduledSubscriptionEndLabel = formatBillingPeriodEnd(effectiveEndDate);
    const cancellationRequestedDateLabel = formatBillingPeriodEnd(cancellationRequestedDate);
    const credits = profile?.credits?.balance;
    const remainingLabel = formatDataAmount(remainingDownloadBytes);
    const usedLabel = formatDataAmount(usedDownloadBytes);
    const limitLabel = formatDataAmount(downloadLimitBytes);
    const usagePercent = getUsagePercent(usedDownloadBytes, downloadLimitBytes);

    // Storage calculations
    const storageRemainingBytes = (storageCapBytes && typeof storageUsageBytes === 'number')
        ? Math.max(storageCapBytes - storageUsageBytes, 0)
        : null;
    const storageRemainingLabel = formatDataAmount(storageRemainingBytes);
    const storageUsedLabel = formatDataAmount(storageUsageBytes);
    const storageCapLabel = formatDataAmount(storageCapBytes);
    const storageUsagePercent = getUsagePercent(storageUsageBytes, storageCapBytes);
    const [resetLoading, setResetLoading] = useState<{ ai: boolean; geocode: boolean }>({ ai: false, geocode: false });
    const [resetFeedback, setResetFeedback] = useState<{ ai?: string; geocode?: string; notice?: string }>({});
    const [showActionsPopover, setShowActionsPopover] = useState(false);
    const popoverRef = React.useRef<HTMLDivElement | null>(null);
    const handleThemeToggle = () => {
        const next =
            themePreference === 'dark'
                ? 'light'
                : themePreference === 'light'
                    ? 'dark'
                    : effectiveTheme === 'dark'
                        ? 'light'
                        : 'dark';
        onThemeChange(next);
    };

    const handleSwitchToBetaSignup = () => {
        setShowAuthModal(false);
        setShowBetaSignupModal(true);
    };

    const handlePrefetchPurchaseModal = () => {
        onPrefetchPurchaseModal?.();
    };

    const handleAuthSuccess = () => {
        setShowAuthModal(false);
    };

    const handleUnsubscribe = async () => {
        try {
            const current = authService.getCurrentUser();
            if (!current) {
                console.error("User not authenticated.");
                return;
            }
            const token = await authService.requireAuthToken({ forceRefresh: true });

            const response = await fetch(`${HTTP_API_URL}/api/auth/unsubscribe`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => null);
                const detail = errorData?.detail || 'Error cancelling subscription.';
                alert(detail);
                throw new Error(detail);
            }

            const data = await response.json().catch(() => null);
            const periodEndLabel = formatBillingPeriodEnd(data?.current_period_end);
            if (periodEndLabel) {
                setUnsubscribeSuccessMessage(
                    `Your subscription will remain active until ${periodEndLabel}. It will then be cancelled automatically.`
                );
            } else {
                setUnsubscribeSuccessMessage(
                    'Your subscription will remain active until the end of your current paid billing period. It will then be cancelled automatically.'
                );
            }
            console.log('Subscription cancellation scheduled successfully');
            setShowUnsubscribeSuccess(true);
        } catch (error) {
            const code = (error as any)?.code;
            if (code === 'email-not-verified') {
                alert('Please verify your email (check your inbox or use the magic link) before managing your subscription.');
            } else {
                console.error("Error while unsubscribing:", error);
            }
        }
    };

    const handleKeepSubscription = async () => {
        try {
            setKeepSubscriptionLoading(true);
            const current = authService.getCurrentUser();
            if (!current) {
                console.error("User not authenticated.");
                return;
            }
            const token = await authService.requireAuthToken({ forceRefresh: true });

            const response = await fetch(`${HTTP_API_URL}/api/auth/resubscribe`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => null);
                const detail = errorData?.detail || 'Error restoring subscription.';
                alert(detail);
                throw new Error(detail);
            }

            const data = await response.json().catch(() => null);
            const periodEndLabel = formatBillingPeriodEnd(data?.current_period_end);
            if (periodEndLabel) {
                setKeepSubscriptionMessage(
                    `Your subscription will continue and renew normally. Next renewal date: ${periodEndLabel}.`
                );
            } else {
                setKeepSubscriptionMessage('Your subscription will continue and renew normally.');
            }
            setShowKeepSubscriptionSuccess(true);
        } catch (error) {
            const code = (error as any)?.code;
            if (code === 'email-not-verified') {
                alert('Please verify your email (check your inbox or use the magic link) before managing your subscription.');
            } else {
                console.error("Error while restoring subscription:", error);
            }
        } finally {
            setKeepSubscriptionLoading(false);
        }
    };

    const handleDeleteAccount = async () => {
        try {
            const current = authService.getCurrentUser();
            if (!current) {
                console.error("User not authenticated.");
                return;
            }
            const token = await authService.requireAuthToken({ forceRefresh: true });

            const response = await fetch(`${HTTP_API_URL}/api/auth/account`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Error deleting the account.');
            }

            console.log('Account deleted successfully');
            // Effettua logout
            await signOut();
            // Ricarica la pagina per uscire dal profilo utente
            window.location.reload();
        } catch (error) {
            const code = (error as any)?.code;
            if (code === 'email-not-verified') {
                alert('Verify your email (or use the magic link) before deleting your account.');
            } else {
                console.error("Error deleting the account:", error);
            }
        }
    };

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && variant === 'drawer' && isOpen && onClose) {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [variant, isOpen, onClose]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (!showActionsPopover) return;
            if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
                setShowActionsPopover(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showActionsPopover]);

    useEffect(() => {
        if (isCollapsed) {
            setShowActionsPopover(false);
        }
    }, [isCollapsed]);

    useEffect(() => {
        if (!isOpen) {
            setShowActionsPopover(false);
        }
    }, [isOpen]);

    const handleResetCache = async (target: 'ai' | 'geocode') => {
        if (!user) {
            setResetFeedback((prev) => ({ ...prev, notice: 'Please log in to reset the analysis cache.' }));
            return;
        }

        const confirmationMessage =
            target === 'ai'
                ? 'This will delete all stored AI analysis entries for identical photos. Continue?'
                : 'This will delete every saved geocoding choice tied to identical photos. Continue?';
        if (!window.confirm(confirmationMessage)) {
            return;
        }

        try {
            setResetLoading((prev) => ({ ...prev, [target]: true }));
            setResetFeedback((prev) => ({ ...prev, notice: undefined, [target]: undefined }));
            const token = await authService.requireAuthToken();
            const response = await fetch(`${HTTP_API_URL}/api/cache/reset`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ type: target }),
            });

            if (!response.ok) {
                const payload = await response.json().catch(() => null);
                throw new Error(payload?.detail || 'Unable to reset the cache.');
            }

            const payload = await response.json().catch(() => ({}));
            const count =
                target === 'ai'
                    ? payload?.ai_cache_deleted ?? 0
                    : payload?.geocode_cache_deleted ?? 0;
            setResetFeedback((prev) => ({
                ...prev,
                [target]:
                    target === 'ai'
                        ? `AI cache cleared (${count} entr${count === 1 ? 'y' : 'ies'} removed).`
                        : `Geocoding cache cleared (${count} entr${count === 1 ? 'y' : 'ies'} removed).`,
            }));
        } catch (error: any) {
            const code = (error as any)?.code;
            if (code === 'email-not-verified') {
                setResetFeedback((prev) => ({
                    ...prev,
                    [target]: 'Verify your email (or use the magic link) before resetting the cache.',
                }));
            } else {
                console.error('Error resetting cache:', error);
                setResetFeedback((prev) => ({
                    ...prev,
                    [target]: error?.message || 'Unable to reset the analysis cache.',
                }));
            }
        } finally {
            setResetLoading((prev) => ({ ...prev, [target]: false }));
        }
    };

    const handleAvatarClick = () => {
        if (variant === 'drawer') {
            setShowActionsPopover((prev) => !prev);
            return;
        }
        if (isCollapsed) {
            onToggleCollapsed?.();
            return;
        }
        setShowActionsPopover((prev) => !prev);
    };

    const handleCollapseToggle = () => {
        setShowActionsPopover(false);
        onToggleCollapsed?.();
    };

    const handleOpenAuthModal = () => {
        if (onRequestAuthModal) {
            onRequestAuthModal();
            return;
        }
        setShowAuthModal(true);
    };

    const proBadge = hasActiveSubscription ? (
        <span className="download-bar__avatar-badge">PRO</span>
    ) : null;

    const asideElement = (
        <aside
            className={`download-bar ${variant} ${isCollapsed ? 'download-bar--collapsed' : ''}`}
            style={{
                width: variant === 'drawer'
                    ? '100%'
                    : isCollapsed
                        ? DOWNLOAD_BAR_COLLAPSED_WIDTH
                        : DOWNLOAD_BAR_WIDTH
            }}
            role={variant === 'drawer' ? 'dialog' : undefined}
            aria-modal={variant === 'drawer' ? true : undefined}
        >
            {variant === 'drawer' && onClose && (
                <button
                    className="download-bar__close-btn"
                    onClick={onClose}
                    aria-label="Close download panel"
                >
                    ✕
                </button>
            )}
            <div className="download-bar__header">
                <button
                    className="download-bar__avatar-button"
                    onClick={handleAvatarClick}
                    aria-label={isCollapsed ? 'Open account panel' : 'Account actions'}
                >
                    <span className={`download-bar__avatar ${hasActiveSubscription ? 'download-bar__avatar--pro' : ''}`}>
                        <span className="download-bar__avatar-icon">👤</span>
                        {proBadge}
                    </span>
                </button>
                <div className="download-bar__title-stack">
                    <p className="download-card__eyebrow">Account</p>
                    <h3 className="download-card__title"></h3>
                </div>
                {variant !== 'drawer' && (
                    <button
                        className="download-bar__collapse-btn"
                        onClick={handleCollapseToggle}
                        aria-label={isCollapsed ? 'Expand account panel' : 'Collapse account panel'}
                    >
                        <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M15 18l-6-6 6-6" />
                        </svg>
                    </button>
                )}
            </div>

            {!isCollapsed && (
                <div className="download-bar__content">
                    <section className="download-card">
                        <header className="download-card__header download-card__header--tight">
                            <div>
                                <p className="download-card__eyebrow">Status</p>
                                <h3 className="download-card__title"></h3>
                            </div>
                            <span className={`download-chip ${user ? 'download-chip--ready' : ''}`}>
                                {user ? 'Connected' : 'Guest'}
                            </span>
                        </header>

                        {user ? (
                            <>
                                <div className="account-panel">
                                    <div className="account-panel__icon">👤</div>
                                    <div>
                                        <p className="account-panel__email">{user.email}</p>
                                        {typeof credits === 'number' && (
                                            <p className="account-panel__credits">{credits} credits</p>
                                        )}
                                        {(remainingLabel || usedLabel) && (
                                            <div className="download-quota">
                                                <div className="download-quota__row">
                                                    <span className="download-quota__label">Downloads left</span>
                                                    <span className="download-quota__value">
                                                        {remainingLabel ?? '—'}
                                                    </span>
                                                </div>
                                                <div className="download-quota__meta">
                                                    {usedLabel && limitLabel ? (
                                                        <span>Used {usedLabel} of {limitLabel}</span>
                                                    ) : limitLabel ? (
                                                        <span>Limit {limitLabel}</span>
                                                    ) : null}
                                                    {Number.isFinite(usagePercent) && downloadLimitBytes ? (
                                                        <span className="download-quota__percent">
                                                            -<br />
                                                            {usagePercent}% used
                                                        </span>
                                                    ) : null}
                                                </div>
                                                <div className="download-quota__progress" aria-hidden="true">
                                                    <span style={{ width: `${usagePercent}%` }} />
                                                </div>
                                            </div>
                                        )}

                                        {(storageRemainingLabel || storageUsedLabel) && (
                                            <div className="download-quota" style={{ marginTop: '12px' }}>
                                                <div className="download-quota__row">
                                                    <span className="download-quota__label">Storage left</span>
                                                    <span className="download-quota__value">
                                                        {storageRemainingLabel ?? '—'}
                                                    </span>
                                                </div>
                                                <div className="download-quota__meta">
                                                    {storageUsedLabel && storageCapLabel ? (
                                                        <span>Used {storageUsedLabel} of {storageCapLabel}</span>
                                                    ) : storageCapLabel ? (
                                                        <span>Limit {storageCapLabel}</span>
                                                    ) : null}
                                                    {Number.isFinite(storageUsagePercent) && storageCapBytes ? (
                                                        <span className="download-quota__percent">
                                                            -<br />
                                                            {storageUsagePercent}% used
                                                        </span>
                                                    ) : null}
                                                </div>
                                                <div className="download-quota__progress" aria-hidden="true">
                                                    <span style={{ width: `${storageUsagePercent}%`, backgroundColor: storageUsagePercent > 90 ? 'var(--download-error, #ef4444)' : undefined }} />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="download-card__actions">
                                    <button
                                        className="download-btn download-btn--ghost"
                                        onClick={async () => {
                                            try {
                                                await signOut();
                                            } catch (error) {
                                                console.error('Error during logout:', error);
                                            }
                                        }}
                                    >
                                        Logout
                                    </button>
                                    <button
                                        className="download-btn download-btn--primary"
                                        onMouseEnter={handlePrefetchPurchaseModal}
                                        onFocus={handlePrefetchPurchaseModal}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handlePrefetchPurchaseModal();
                                            setShowPurchaseModal(true);
                                        }}
                                    >
                                        Buy Credits
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div className="download-card__actions download-card__actions--center">
                                <button
                                    className="download-btn download-btn--primary download-btn--full"
                                    onClick={handleOpenAuthModal}
                                >
                                    Login or Register
                                </button>
                            </div>
                        )}
                    </section>
                    {!user && (
                        <section className="download-card">
                            <header className="download-card__header download-card__header--tight">
                                <div>
                                    <p className="download-card__eyebrow">Welcome</p>
                                    <h3 className="download-card__title">Free Starter Credits</h3>
                                </div>
                            </header>
                            <p style={{ margin: 0, color: 'var(--download-text-muted, #4a4a4a)', lineHeight: 1.5 }}>
                                Register now and get 5 free credits to analyze your first images. No credit card required. (One-time offer per email)
                            </p>
                        </section>
                    )}
                </div>
            )}
            {showActionsPopover && (
                <div className="download-bar__actions-popover" ref={popoverRef}>
                    <div className="account-actions">
                        <div className="account-actions__block">
                            <p className="account-actions__title">Appearance</p>
                            <p className="account-actions__helper">
                                Switch UI theme; defaults to your device preference.
                            </p>
                            <div className="account-actions__buttons account-actions__buttons--row">
                                <button
                                    className="download-btn download-btn--ghost"
                                    onClick={handleThemeToggle}
                                >
                                    {themePreference === 'system'
                                        ? `Toggle (${effectiveTheme === 'dark' ? 'Dark' : 'Light'})`
                                        : themePreference === 'dark'
                                            ? 'Use Light'
                                            : 'Use Dark'}
                                </button>
                                <button
                                    className="download-btn download-btn--ghost"
                                    onClick={() => onThemeChange('system')}
                                >
                                    {themePreference === 'system' ? 'System (default)' : 'Follow system'}
                                </button>
                            </div>
                        </div>
                        {user && (
                            <div className="account-actions__block">
                                <p className="account-actions__title">Reset analysis cache</p>
                                <p className="account-actions__helper">Refresh AI and geocode decisions for identical photos.</p>
                                {resetFeedback.notice && (
                                    <p className="account-actions__feedback account-actions__feedback--error">
                                        {resetFeedback.notice}
                                    </p>
                                )}
                                <div className="account-actions__buttons">
                                    <button
                                        className="download-btn download-btn--ghost"
                                        disabled={resetLoading.ai}
                                        onClick={() => handleResetCache('ai')}
                                    >
                                        {resetLoading.ai ? 'Resetting AI…' : 'Reset AI cache'}
                                    </button>
                                    {resetFeedback.ai && (
                                        <p className="account-actions__feedback">{resetFeedback.ai}</p>
                                    )}
                                    <button
                                        className="download-btn download-btn--ghost"
                                        disabled={resetLoading.geocode}
                                        onClick={() => handleResetCache('geocode')}
                                    >
                                        {resetLoading.geocode ? 'Resetting geocode…' : 'Reset geocode cache'}
                                    </button>
                                    {resetFeedback.geocode && (
                                        <p className="account-actions__feedback">{resetFeedback.geocode}</p>
                                    )}
                                </div>
                            </div>
                        )}
                        {profile?.subscription?.status === 'active' && !isCancellationAlreadyScheduled && (
                            <button
                                className="account-actions__link account-actions__link--danger"
                                onClick={(e) => {
                                    e.preventDefault();
                                    setShowActionsPopover(false);
                                    setShowUnsubscribeModal(true);
                                }}
                            >
                                Unsubscribe
                            </button>
                        )}
                        {profile?.subscription?.status === 'active' && isCancellationAlreadyScheduled && (
                            <div className="account-actions__block">
                                <p className="account-actions__title">Subscription</p>
                                <p className="account-actions__helper">
                                    {scheduledSubscriptionEndLabel
                                        ? `Cancellation scheduled for ${scheduledSubscriptionEndLabel}.`
                                        : 'Cancellation scheduled for the end of your current paid billing period.'}
                                </p>
                                <button
                                    className="download-btn download-btn--primary"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setShowActionsPopover(false);
                                        setShowKeepSubscriptionConfirm(true);
                                    }}
                                >
                                    Keep subscription
                                </button>
                            </div>
                        )}
                        {user && (
                            <button
                                className="account-actions__link account-actions__link--danger"
                                onClick={(e) => {
                                    e.preventDefault();
                                    setShowActionsPopover(false);
                                    setShowDeleteAccountModal(true);
                                }}
                            >
                                Delete account
                            </button>
                        )}
                    </div>
                </div>
            )}
        </aside>
    );

    const dialogModals = (
        <>
            {/* Modali per autenticazione */}
            <DraggableModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)}>
                <div className="modal-shell modal-shell--wide">
                    <h4 className="modal-handle">🔐 Authentication Required</h4>
                    <p>Please log in with one of the available methods before continuing.</p>
                    <React.Suspense fallback={<div className="auth-modal__hint">Loading authentication…</div>}>
                        <AuthModal
                            onClose={() => setShowAuthModal(false)}
                            onSwitchToBetaSignup={handleSwitchToBetaSignup}
                            onLoginSuccess={handleAuthSuccess}
                        />
                    </React.Suspense>
                </div>
            </DraggableModal>

            <DraggableModal isOpen={showBetaSignupModal} onClose={() => setShowBetaSignupModal(false)}>
                <React.Suspense fallback={<div className="auth-modal__hint">Loading…</div>}>
                    <BetaSignupModal onClose={() => setShowBetaSignupModal(false)} />
                </React.Suspense>
            </DraggableModal>

            <React.Suspense fallback={<div className="auth-modal__hint">Loading subscriptions…</div>}>
                <UnsubscribeModal
                    isOpen={showUnsubscribeModal}
                    onClose={() => setShowUnsubscribeModal(false)}
                    onConfirm={handleUnsubscribe}
                    isAlreadyCancelled={isCancellationAlreadyScheduled}
                    scheduledEndDateLabel={scheduledSubscriptionEndLabel}
                    cancellationRequestedDateLabel={cancellationRequestedDateLabel}
                />
            </React.Suspense>

            <DraggableModal
                isOpen={showKeepSubscriptionConfirm}
                onClose={() => setShowKeepSubscriptionConfirm(false)}
                presentation="dialog"
            >
                <div
                    className="modal-shell"
                    style={{
                        textAlign: 'center',
                        width: 'min(480px, 92vw)',
                        minWidth: 'min(320px, 92vw)',
                        maxWidth: 'min(480px, 92vw)',
                    }}
                >
                    <h3>Keep subscription?</h3>
                    <p>
                        Your cancellation will be reversed. Your subscription will continue and renew normally.
                        Do you want to keep your subscription?
                    </p>
                    <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-around' }}>
                        <button
                            onClick={() => setShowKeepSubscriptionConfirm(false)}
                            style={{
                                backgroundColor: '#ccc',
                                color: 'black',
                                padding: '10px 20px',
                                border: 'none',
                                borderRadius: '5px',
                                cursor: 'pointer'
                            }}
                            disabled={keepSubscriptionLoading}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={async () => {
                                await handleKeepSubscription();
                                setShowKeepSubscriptionConfirm(false);
                            }}
                            style={{
                                backgroundColor: '#2563eb',
                                color: 'white',
                                padding: '10px 20px',
                                border: 'none',
                                borderRadius: '5px',
                                cursor: 'pointer'
                            }}
                            disabled={keepSubscriptionLoading}
                        >
                            {keepSubscriptionLoading ? 'Keeping…' : 'Keep subscription'}
                        </button>
                    </div>
                </div>
            </DraggableModal>

            <React.Suspense fallback={<div className="auth-modal__hint">Loading account options…</div>}>
                <DeleteAccountModal
                    isOpen={showDeleteAccountModal}
                    onClose={() => setShowDeleteAccountModal(false)}
                    onConfirm={handleDeleteAccount}
                />
            </React.Suspense>
            <DraggableModal
                isOpen={showUnsubscribeSuccess}
                onClose={() => setShowUnsubscribeSuccess(false)}
                presentation="dialog"
            >
                <div
                    className="modal-shell"
                    style={{
                        textAlign: 'center',
                        width: 'min(420px, 92vw)',
                        minWidth: 'min(320px, 92vw)',
                        maxWidth: 'min(420px, 92vw)',
                    }}
                >
                    <h3>Unsubscribe request received</h3>
                    <p>{unsubscribeSuccessMessage}</p>
                    <button
                        className="download-btn download-btn--primary"
                        style={{ marginTop: '20px' }}
                        onClick={() => {
                            setShowUnsubscribeSuccess(false);
                            window.location.reload();
                        }}
                    >
                        Ok
                    </button>
                </div>
            </DraggableModal>
            <DraggableModal
                isOpen={showKeepSubscriptionSuccess}
                onClose={() => setShowKeepSubscriptionSuccess(false)}
                presentation="dialog"
            >
                <div
                    className="modal-shell"
                    style={{
                        textAlign: 'center',
                        width: 'min(420px, 92vw)',
                        minWidth: 'min(320px, 92vw)',
                        maxWidth: 'min(420px, 92vw)',
                    }}
                >
                    <h3>Subscription restored</h3>
                    <p>{keepSubscriptionMessage}</p>
                    <button
                        className="download-btn download-btn--primary"
                        style={{ marginTop: '20px' }}
                        onClick={() => {
                            setShowKeepSubscriptionSuccess(false);
                            window.location.reload();
                        }}
                    >
                        Ok
                    </button>
                </div>
            </DraggableModal>
        </>
    );

    if (variant === 'drawer') {
        if (!isOpen) return dialogModals;

        return (
            <>
                <div className="download-bar__overlay" onClick={onClose}>
                    <div onClick={(e) => e.stopPropagation()}>
                        {asideElement}
                    </div>
                </div>
                {dialogModals}
            </>
        );
    }

    return (
        <>
            {asideElement}
            {dialogModals}
        </>
    );
};

export default DownloadBar;
